import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Label,
  Input,
  Row,
  Table,
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import NumberFormat from "react-number-format";
import Datetime from "react-datetime";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import PageHeader from "../common/PageHeader";
import ButtonIcon from "../common/ButtonIcon";
import withRedirect from "../../helpers/hoc/withRedirect";
import SupportLink from "../root/support/components/SupportLink";
import ItemImageTitleSubProperty from "../common/ItemImageTitleSubProperty";
import { PreloadState } from "../UI/preloading/PreloadState";
import { CurrencyContext } from "../../context/Context";
import {
  fetchDebtsToPayAsPropertyCuentaPendingPeriodTest,
  fetchDebtsToPayAsPropertyCuentaPendingPeriod,
  fetchDebtsToPayAsPropertyPendingTotal,
} from "../../helpers/api/finance";
import { fetchOneProperty, findOneConfig, findConfigAsProperty } from "../../helpers/api/condominium";
import { fetchAllPages } from "../../helpers/api/pagination";
import useSWRFetch from "../../helpers/hooks/useSWRFetch";
import useModuleBase from "../../helpers/hooks/useModuleBase";
import VoidInitialDebtModal from "../properties/VoidInitialDebtModal";

const periodDate = (period) =>
  period?.end_at || period?.created_at || period?.accounts?.[0]?.created_at || null;

const toMoment = (value) => {
  if (!value) return null;
  const parsed = moment.isMoment(value) ? value : moment(value);
  return parsed.isValid() ? parsed : null;
};

const dateInputValue = (value) =>
  moment.isMoment(value) ? value.format("DD/MM/YYYY") : value ?? "";

const personOf = (record) => record?.user?.data || record?.user || null;

const fullName = (record) => {
  const person = personOf(record);
  return [person?.firstname, person?.lastname].filter(Boolean).join(" ") || null;
};

const Money = ({ value, prefix, className }) => (
  <NumberFormat
    className={className}
    value={Number(value) || 0}
    prefix={prefix ? `${prefix} ` : undefined}
    thousandSeparator
    decimalScale={2}
    fixedDecimalScale
    displayType="text"
  />
);

export const AccountStatement = ({ ownerView = false, ownerProfile = null, children }) => {
  const moduleBase = useModuleBase();
  const location = useLocation();
  const debt = ownerView
    ? { property: ownerProfile?.property, user: { data: ownerProfile?.data }, aliquot: ownerProfile?.aliquot }
    : location?.state?.debt;
  const history = useHistory();
  const statementRef = useRef(null);
  const { property, id } = useParams();
  const { role } = useSelector((state) => state.auth.user);
  const { selectedCondominium, condominium, address, phone } = useSelector(
    (state) => state.condominium
  );
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const condominiumId = ownerView
    ? ownerProfile?.condominium?.condominium_id ?? ownerProfile?.condominium?.id ?? ownerProfile?.condominium_id
    : id || selectedCondominium;
  const condominiumName = ownerView ? ownerProfile?.condominium?.name : condominium;
  const condominiumAddress = ownerView ? ownerProfile?.condominium?.address : address;
  const condominiumDocument = ownerView ? ownerProfile?.condominium?.identification : phone;
  const issuedAt = moment();

  const [formDate, setformDate] = useState({ since: null, to: null });
  const [appliedRange, setAppliedRange] = useState({ since: null, to: null });
  const [paymentState, setPaymentState] = useState("0");
  const showingPaid = ownerView && paymentState === "1";
  const [amount, setAmount] = useState(0);
  const [amountBs, setAmountBs] = useState(null);
  const [amountLoading, setAmountLoading] = useState(true);
  const [amountError, setAmountError] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(false);
  const [propertyDetail, setPropertyDetail] = useState(null);
  const [voidPeriodId, setVoidPeriodId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { data: dataConfig, isLoading: loadingConfig } = useSWRFetch(
    condominiumId ? [ownerView ? "owner-statement-config" : "statement-config", condominiumId] : null,
    (_, condoId) => ownerView ? findConfigAsProperty(condoId) : findOneConfig(condoId)
  );

  useEffect(() => {
    let isMounted = true;
    if (ownerView || !property) return undefined;

    fetchOneProperty({ propertyId: property })
      .then((response) => {
        if (isMounted) setPropertyDetail(response?.data || null);
      })
      .catch(() => {
        if (isMounted) setPropertyDetail(null);
      });

    return () => {
      isMounted = false;
    };
  }, [property, ownerView]);

  useEffect(() => {
    let isMounted = true;
    if (!condominiumId || (!ownerView && !property)) return undefined;

    setRowsLoading(true);
    setRowsError(false);
    fetchAllPages((page, limit) =>
      (ownerView ? fetchDebtsToPayAsPropertyCuentaPendingPeriod : fetchDebtsToPayAsPropertyCuentaPendingPeriodTest)(page, limit, {
        condominiumId,
        ...(ownerView && { state: paymentState }),
        ...(!ownerView && { property_id: property }),
      })
    )
      .then((response) => {
        if (!Array.isArray(response?.data) ||
          (response.meta?.total != null && Number(response.meta.total) > response.data.length)) {
          throw new Error("No se pudo cargar el listado completo del estado de cuenta.");
        }
        if (isMounted) setRows(response.data);
      })
      .catch(() => {
        if (isMounted) {
          setRows([]);
          setRowsError(true);
        }
      })
      .finally(() => {
        if (isMounted) setRowsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [condominiumId, property, ownerView, paymentState, reloadKey]);

  useEffect(() => {
    let isMounted = true;
    if (!condominiumId || (!ownerView && !property)) return undefined;

    setAmountLoading(true);
    setAmountError(false);
    fetchDebtsToPayAsPropertyPendingTotal({
      condominiumId,
      ...(!ownerView && { property_id: property }),
    })
      .then(({ total_amount, total_amount_bs }) => {
        if (isMounted) {
          setAmount(total_amount);
          setAmountBs(total_amount_bs ?? null);
        }
      })
      .catch(() => {
        if (isMounted) {
          setAmountError(true);
        }
      })
      .finally(() => {
        if (isMounted) setAmountLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [condominiumId, property, ownerView, reloadKey]);

  const ownerRecord =
    propertyDetail ||
    debt ||
    rows.find((row) => row?.accounts?.[0]?.property)?.accounts?.[0]?.property;
  const owner = personOf(ownerRecord);
  const ownerName = fullName(ownerRecord) || "No disponible";
  const unitName =
    (typeof propertyDetail?.property === "string" && propertyDetail.property) ||
    (typeof debt?.property === "string" && debt.property) ||
    rows[0]?.accounts?.[0]?.property?.property ||
    "No disponible";
  const aliquotValue =
    propertyDetail?.aliquot?.value ?? debt?.aliquot?.value ?? debt?.aliquot;
  const localIso = curr?.currency?.iso || "";
  const baseSymbol = baseCurrency?.symbol || "$";
  const hasRange = Boolean(appliedRange.since || appliedRange.to);

  const visibleRows = useMemo(() => {
    const sorted = [...rows].sort((left, right) => {
      const leftDate = toMoment(periodDate(left));
      const rightDate = toMoment(periodDate(right));
      if (!leftDate && !rightDate) return 0;
      if (!leftDate) return 1;
      if (!rightDate) return -1;
      return leftDate.valueOf() - rightDate.valueOf();
    });

    if (!hasRange) return sorted;

    return sorted.filter((row) => {
      const date = toMoment(periodDate(row));
      if (!date) return true;
      if (appliedRange.since && date.isBefore(appliedRange.since, "day")) return false;
      if (appliedRange.to && date.isAfter(appliedRange.to, "day")) return false;
      return true;
    });
  }, [rows, appliedRange, hasRange]);

  const localOf = (row) =>
    row?.total_amount_bs ??
    Number(row?.total_amount || 0) * Number(curr?.value || 0);

  const filteredTotal = visibleRows.reduce(
    (sum, row) => sum + Number(row?.total_amount || 0),
    0
  );
  const filteredLocal = visibleRows.reduce((sum, row) => sum + Number(localOf(row) || 0), 0);
  const statementTotal = hasRange && !showingPaid ? filteredTotal : Number(amount) || filteredTotal;
  const statementLocal = hasRange && !showingPaid
    ? filteredLocal
    : amountBs ?? (filteredLocal || statementTotal * Number(curr?.value || 0));
  const isSettled = !amountLoading && !rowsLoading && !amountError && !rowsError && Number(statementTotal) === 0;

  let runningBalance = 0;
  const statementLines = visibleRows.map((row) => {
    runningBalance += Number(row?.total_amount || 0);
    const accounts = Array.isArray(row?.accounts) ? row.accounts : [];
    const details = accounts
      .map((account) => account?.description || account?.name || account?.finance?.name)
      .filter(Boolean);
    return {
      row,
      balance: runningBalance,
      details: details.slice(0, 3),
      hiddenDetails: Math.max(details.length - 3, 0),
      canVoid:
        !ownerView && role === "manager" &&
        accounts.some((account) => account.category === "initial_debt"),
    };
  });

  const periodLabel = hasRange
    ? `Del ${
        appliedRange.since ? appliedRange.since.format("DD/MM/YYYY") : "inicio"
      } al ${appliedRange.to ? appliedRange.to.format("DD/MM/YYYY") : "hoy"}`
    : `${showingPaid ? "Cuentas pagadas" : "Saldos pendientes"} al ${issuedAt.format("DD/MM/YYYY")}`;

  const handlePrint = useReactToPrint({
    content: () => statementRef.current,
    documentTitle: `Estado de cuenta - ${unitName}`,
    pageStyle: `
      @page { size: A4; margin: 12mm; }
      @media print {
        .d-print-none { display: none !important; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .statement-sheet .statement-summary {
          flex-wrap: nowrap !important;
          break-inside: avoid;
        }
        .statement-sheet .statement-summary > div {
          flex: 0 0 33.333333% !important;
          max-width: 33.333333% !important;
          margin-bottom: 0 !important;
        }
        .statement-sheet .statement-status {
          border: 0 !important;
          box-shadow: none !important;
        }
      }
    `,
  });

  const openDetail = (period) => {
    history.push({
      pathname: ownerView
        ? `/dashboard/my/finance/topay/${period.id}`
        : `/dashboard/${moduleBase}/${id}/receivable/detail/${period.id}`,
      state: {
        property,
        period_id: period.id,
        debt: period,
        cost: debt,
      },
    });
  };

  return (
    <>
      <PageHeader
        title="Estado de cuenta"
        titleTag="h2"
        description={ownerView
          ? "Consulta tus saldos pendientes e imprime tu estado de cuenta."
          : "Consulta los saldos pendientes de la propiedad e imprime el estado de cuenta."}
        className="mb-3 d-print-none"
        col={{ lg: 8 }}
      >
        {!ownerView && <SupportLink />}
      </PageHeader>

      {children}

      <Card className="mb-3 d-print-none">
        <CardBody>
          <Row className="align-items-end">
            {ownerView && (
              <Col sm className="mb-2">
                <Label for="statement-payment-state" className="mb-1 fs--1">Estado de pago</Label>
                <Input
                  id="statement-payment-state"
                  type="select"
                  value={paymentState}
                  onChange={(event) => setPaymentState(event.target.value)}
                >
                  <option value="0">No pagados</option>
                  <option value="1">Pagados</option>
                </Input>
              </Col>
            )}
            {!ownerView && (
              <Col xs="auto" className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon={faFilter} className="text-600" />
              </Col>
            )}
            <Col sm={ownerView ? true : "auto"} className={ownerView ? "mb-2 pr-sm-2" : "mb-2"}>
              <Label className="mb-1 fs--1">Desde</Label>
              <Datetime
                value={formDate.since ?? ""}
                onChange={(value) => setformDate((prev) => ({ ...prev, since: value }))}
                dateFormat="DD/MM/YYYY"
                timeFormat={false}
                inputProps={{ placeholder: "DD/MM/AAAA", value: dateInputValue(formDate.since) }}
              />
            </Col>
            <Col sm={ownerView ? true : "auto"} className={ownerView ? "mb-2 px-sm-2" : "mb-2"}>
              <Label className="mb-1 fs--1">Hasta</Label>
              <Datetime
                value={formDate.to ?? ""}
                onChange={(value) => setformDate((prev) => ({ ...prev, to: value }))}
                isValidDate={(current) =>
                  !formDate.since || current.isSameOrAfter(formDate.since, "day")
                }
                dateFormat="DD/MM/YYYY"
                timeFormat={false}
                inputProps={{ placeholder: "DD/MM/AAAA", value: dateInputValue(formDate.to) }}
              />
            </Col>
            <Col xs="auto" className={ownerView ? "mb-2 d-flex pl-sm-2 ml-auto" : "mb-2 d-flex"}>
              <ButtonIcon
                icon={faSearch}
                color="falcon-default"
                className="mr-2"
                onClick={() =>
                  setAppliedRange({
                    since: toMoment(formDate.since),
                    to: toMoment(formDate.to),
                  })
                }
              >
                Filtrar
              </ButtonIcon>
              {hasRange && (
                <Button
                  color="falcon-default"
                  onClick={() => {
                    setformDate({ since: null, to: null });
                    setAppliedRange({ since: null, to: null });
                  }}
                >
                  Limpiar
                </Button>
              )}
            </Col>
            {ownerView && <div className="w-100 mt-2" />}
            <Col className="mb-2 d-flex justify-content-end flex-wrap">
              <ButtonIcon
                icon="print"
                color="falcon-default"
                className="mr-2 mb-2 mb-sm-0"
                onClick={handlePrint}
                disabled={rowsLoading || amountLoading || loadingConfig || rowsError || amountError}
              >
                Imprimir
              </ButtonIcon>
              {(ownerView || role === "manager") && (
                <ButtonIcon
                  color="primary"
                  icon={faPlus}
                  onClick={() =>
                    history.push({
                      pathname: ownerView
                        ? "/dashboard/my/finance/topays"
                        : `/dashboard/${moduleBase}/${id}/receivable/topays/${property}`,
                    })
                  }
                >
                  {ownerView ? "Pagar cuentas" : "Registrar pago"}
                </ButtonIcon>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card innerRef={statementRef} className="mb-3 statement-sheet">
        <div
          style={{
            height: 6,
            background: "#a02cc7",
            printColorAdjust: "exact",
            WebkitPrintColorAdjust: "exact",
          }}
        />
        <CardBody>
          <ItemImageTitleSubProperty
            image={dataConfig?.data?.logo}
            loading={loadingConfig}
            title1="Estado de cuenta"
            titleCondominium={condominiumName ? `Condominio: ${condominiumName}` : "Estado de cuenta"}
            condominium={[
              { label: "Documento", value: condominiumDocument || "No disponible" },
              { label: "Dirección", value: condominiumAddress || "No disponible" },
            ]}
            titleProperty="Datos del inmueble"
            property={[
              { label: "Propietario", value: ownerName },
              { label: "Inmueble", value: unitName },
              { label: "Teléfono", value: owner?.phone || "No disponible" },
              { label: "Correo", value: owner?.email || "No disponible" },
              ...(Number.isFinite(Number(aliquotValue)) && aliquotValue !== ""
                ? [{ label: "Alícuota", value: Number(aliquotValue).toFixed(4) }]
                : []),
            ]}
            titleInvoice="Datos del estado"
            invoice={[
              { label: "Fecha de emisión", value: issuedAt.format("DD/MM/YYYY") },
              { label: "Período", value: periodLabel },
              {
                label: "Estado",
                value: rowsError || amountError
                  ? "No disponible"
                  : rowsLoading || amountLoading
                    ? "Cargando..."
                    : isSettled ? "Al día" : "Con saldo pendiente",
              },
              {
                label: "Documentos",
                value: rowsLoading ? "..." : String(visibleRows.length),
              },
            ]}
          />

          {amountError && (
            <Alert color="danger">
              No se pudo cargar el saldo pendiente. Vuelve a cargar el estado de cuenta.
            </Alert>
          )}
          {!amountError && !rowsError && <Row className="mb-3 statement-summary">
            <Col md={4} className="mb-2 mb-md-0">
              <div className="border border-200 rounded h-100 p-3">
                <div className="fs--1 text-600 font-weight-semi-bold">Saldo pendiente</div>
                <div className={`fs-1 font-weight-bold mb-0 ${isSettled ? "text-success" : "text-danger"}`}>
                  <PreloadState cond={!amountLoading} loading={amountLoading} loadingClass="p-0">
                    <Money value={statementTotal} prefix={baseSymbol} />
                  </PreloadState>
                </div>
              </div>
            </Col>
            <Col md={4} className="mb-2 mb-md-0">
              <div className="border border-200 rounded h-100 p-3">
                <div className="fs--1 text-600 font-weight-semi-bold">
                  Equivalente {localIso}
                </div>
                <div className="fs-1 font-weight-bold text-900 mb-0">
                  <PreloadState cond={!amountLoading} loading={amountLoading} loadingClass="p-0">
                    <Money value={statementLocal} prefix={localIso} />
                  </PreloadState>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="border border-200 rounded h-100 p-3">
                <div className="fs--1 text-600 font-weight-semi-bold">Situación</div>
                <div className="mt-1">
                  <Badge color={isSettled ? "success" : "warning"} className="fs--1 statement-status">
                    {isSettled ? "Al día" : "Pendiente de pago"}
                  </Badge>
                </div>
                <p className="fs--1 text-600 mb-0 mt-2">
                  {showingPaid
                    ? "Saldo pendiente general de la propiedad, independiente de las cuentas pagadas que se muestran abajo."
                    : hasRange
                    ? "Totales del período filtrado."
                    : "Incluye todos los saldos abiertos de la propiedad."}
                </p>
              </div>
            </Col>
          </Row>}

          {rowsError ? (
            <Alert color="danger" className="mb-0">
              No se pudo cargar el estado de cuenta. Intenta de nuevo más tarde.
            </Alert>
          ) : (
            <PreloadState cond={!rowsLoading} loading={rowsLoading}>
              {statementLines.length > 0 ? (
                <div className="table-responsive">
                  <Table bordered className="fs--1 mb-0">
                    <thead className="bg-200">
                      <tr>
                        <th className="border-0">Fecha</th>
                        <th className="border-0">Concepto</th>
                        <th className="border-0">N° de control</th>
                        <th className="border-0 text-right">Cargo pendiente</th>
                        {localIso && (
                          <th className="border-0 text-right">{localIso}</th>
                        )}
                        <th className="border-0 text-right">Saldo</th>
                        <th className="border-0 text-right d-print-none">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statementLines.map(({ row, balance, details, hiddenDetails, canVoid }) => {
                        const date = toMoment(periodDate(row));
                        return (
                          <tr
                            key={row.id}
                            className="cursor-pointer"
                            role="link"
                            tabIndex={0}
                            aria-label={`Ver detalle de ${row?.name || "cuenta"}`}
                            onClick={(event) => {
                              if (!event.target.closest("[data-statement-actions]")) {
                                openDetail(row);
                              }
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" && event.target === event.currentTarget) {
                                event.preventDefault();
                                openDetail(row);
                              }
                            }}
                          >
                            <td className="align-middle text-nowrap">
                              {date ? date.format("DD/MM/YYYY") : "—"}
                            </td>
                            <td className="align-middle">
                              <div className="font-weight-semi-bold text-900">{row?.name || "Cuenta"}</div>
                              {details.length > 0 && (
                                <div className="text-600">
                                  {details.join(" · ")}
                                  {hiddenDetails > 0 ? ` · y ${hiddenDetails} más` : ""}
                                </div>
                              )}
                            </td>
                            <td className="align-middle">{row?.control_number || "—"}</td>
                            <td className="align-middle text-right text-nowrap">
                              <Money value={row?.total_amount} prefix={baseSymbol} />
                            </td>
                            {localIso && (
                              <td className="align-middle text-right text-nowrap">
                                <Money value={localOf(row)} prefix={localIso} />
                              </td>
                            )}
                            <td className="align-middle text-right text-nowrap font-weight-semi-bold">
                              <Money value={balance} prefix={baseSymbol} />
                            </td>
                            <td
                              className="align-middle text-right d-print-none"
                              data-statement-actions
                            >
                              <UncontrolledDropdown direction="left" className="d-inline-block">
                                <DropdownToggle
                                  color="falcon-default"
                                  size="sm"
                                  aria-label={`Acciones de ${row?.name || "cuenta"}`}
                                >
                                  <FontAwesomeIcon icon="ellipsis-h" />
                                </DropdownToggle>
                                <DropdownMenu>
                                  <DropdownItem onClick={() => openDetail(row)}>
                                    Ver detalle
                                  </DropdownItem>
                                  {canVoid && (
                                    <DropdownItem
                                      className="text-danger"
                                      onClick={() => setVoidPeriodId(row.id)}
                                    >
                                      Anular deuda inicial
                                    </DropdownItem>
                                  )}
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan={3} className="text-right">
                          Saldo a la fecha
                        </th>
                        <th className="text-right text-nowrap">
                          <Money value={hasRange || showingPaid ? filteredTotal : statementTotal} prefix={baseSymbol} />
                        </th>
                        {localIso && (
                          <th className="text-right text-nowrap">
                            <Money value={hasRange || showingPaid ? filteredLocal : statementLocal} prefix={localIso} />
                          </th>
                        )}
                        <th className="text-right text-nowrap">
                          <Money value={hasRange || showingPaid ? filteredTotal : statementTotal} prefix={baseSymbol} />
                        </th>
                        <th className="d-print-none" />
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              ) : (
                <Alert color={isSettled ? "success" : "info"} className="mb-0">
                  {showingPaid
                    ? "No hay cuentas pagadas para el período seleccionado."
                    : isSettled
                    ? "Esta propiedad no tiene saldos pendientes."
                    : "No hay movimientos para el período seleccionado."}
                </Alert>
              )}
            </PreloadState>
          )}

          <p className="fs--1 text-600 mt-3 mb-0">
            {showingPaid
              ? "Mostrando cuentas pagadas. Los importes de la tabla representan su saldo pendiente, no el monto original pagado."
              : "Este estado de cuenta relaciona los cargos que todavía tienen saldo. Los abonos ya aplicados no se listan como movimientos."}
          </p>

          <Row className="mt-5">
            <Col sm={6} className="mb-4">
              <div style={{ borderTop: "1px solid #748194", width: 220, paddingTop: 6 }}>
                <span className="fs--1 text-700">Administración</span>
              </div>
            </Col>
            <Col sm={6} className="mb-4">
              <div style={{ borderTop: "1px solid #748194", width: 220, paddingTop: 6 }}>
                <span className="fs--1 text-700">Propietario</span>
              </div>
            </Col>
          </Row>
          <p className="fs--2 text-500 mb-0">
            Documento generado por Homelan el {issuedAt.format("DD/MM/YYYY HH:mm")}. No
            sustituye un comprobante de pago.
          </p>
        </CardBody>
      </Card>

      {!ownerView && <VoidInitialDebtModal
        isOpen={Boolean(voidPeriodId)}
        toggle={() => setVoidPeriodId(null)}
        condominiumId={condominiumId}
        propertyId={property}
        periodId={voidPeriodId}
        onVoided={() => setReloadKey((key) => key + 1)}
      />}
    </>
  );
};

export default withRedirect(AccountStatement);
